import React, { useEffect, useMemo, useState } from "react";
import { D, st } from "../../constants";
import {
  LABOR_RATES,
  MATERIAL_MARKUP,
  DRIVER_HEADROOM,
  CLASS_2_MAX_WATTS,
  NON_CLASS_2_LABOR_MIN_PER_DRIVER,
  CHANNEL_OPTIONS,
  STRIP_OPTIONS,
  DRIVER_OPTIONS,
  CONNECTOR_COST_PER_FT,
  currency,
} from "./ledRates";

function recommendDriverCount(feet, stripId, driverId) {
  const ft = Math.max(0, Number(feet) || 0);
  const s = STRIP_OPTIONS.find((s) => s.id === stripId) || STRIP_OPTIONS[0];
  const d = DRIVER_OPTIONS.find((d) => d.id === driverId) || DRIVER_OPTIONS[0];
  const totalWatts = ft * (s.wattsPerFt || 0);
  const driverWattsEach = d.watts || 0;
  return driverWattsEach > 0 ? Math.max(1, Math.ceil(totalWatts / (driverWattsEach * DRIVER_HEADROOM))) : 0;
}

export default function LedBidTool({ controllerTotal = 0, onCalcChange }) {
  const [feet, setFeet] = useState(60);
  const [channelId, setChannelId] = useState("surface");
  const [stripId, setStripId] = useState("generic-standard");
  const [complexity, setComplexity] = useState(1.0);
  const [driverId, setDriverId] = useState("generic-60w");
  const [driverCount, setDriverCount] = useState(() => recommendDriverCount(60, "generic-standard", "generic-60w"));
  const [newCircuit, setNewCircuit] = useState(true);
  const [circuitHours, setCircuitHours] = useState(2);

  const channel = CHANNEL_OPTIONS.find((c) => c.id === channelId) || CHANNEL_OPTIONS[0];
  const strip = STRIP_OPTIONS.find((s) => s.id === stripId) || STRIP_OPTIONS[0];
  const driver = DRIVER_OPTIONS.find((d) => d.id === driverId) || DRIVER_OPTIONS[0];

  useEffect(() => {
    setDriverCount(recommendDriverCount(feet, stripId, driverId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feet, stripId, driverId]);

  const calc = useMemo(() => {
    const ft = Math.max(0, Number(feet) || 0);
    const dCount = Math.max(0, Number(driverCount) || 0);
    const cHours = Math.max(0, Number(circuitHours) || 0);

    const materials = [
      { label: "LED strip", cost: ft * strip.costPerFt },
      { label: "Aluminum channel + diffuser", cost: ft * channel.costPerFt },
      { label: "Connectors/solder/clips", cost: ft * CONNECTOR_COST_PER_FT },
      { label: "Driver(s)", cost: dCount * driver.cost },
    ];
    const materialsCost = materials.reduce((s, m) => s + m.cost, 0);
    const materialsSell = materialsCost * (1 + MATERIAL_MARKUP);

    const mountingHrs = (ft * channel.mountMinPerFt * Number(complexity)) / 60;
    const wiringHrs = (ft * 2) / 60;
    const driverInstallHrs = (dCount * 30) / 60;
    const circuitLaborHrs = newCircuit ? cHours : 0;
    const nonClass2Hrs = (!driver.classII && dCount > 0) ? (dCount * NON_CLASS_2_LABOR_MIN_PER_DRIVER) / 60 : 0;

    const labor = [
      { label: "Strip & channel mounting", hrs: mountingHrs, rate: LABOR_RATES.installer },
      { label: "Wiring & terminations", hrs: wiringHrs, rate: LABOR_RATES.installer },
      { label: "Driver install & wiring", hrs: driverInstallHrs, rate: LABOR_RATES.electrician },
      { label: "Class 1 wiring method (conduit) — driver exceeds Class 2 limit", hrs: nonClass2Hrs, rate: LABOR_RATES.electrician },
      { label: "New circuit/junction", hrs: circuitLaborHrs, rate: LABOR_RATES.electrician },
    ];
    const laborCost = labor.reduce((s, l) => s + l.hrs * l.rate, 0);
    const total = materialsSell + laborCost + Math.max(0, Number(controllerTotal) || 0);

    const totalWatts = ft * (strip.wattsPerFt || 0);
    const driverWattsEach = driver.watts || 0;
    const driverCapacity = dCount * driverWattsEach * DRIVER_HEADROOM;
    const wattsOk = totalWatts === 0 ? true : driverCapacity >= totalWatts;
    const recommendedDriverCount = recommendDriverCount(feet, stripId, driverId);

    return {
      materials, materialsSell, labor, laborCost, total, ft,
      totalWatts, driverCapacity, wattsOk, recommendedDriverCount,
    };
  }, [feet, channel, strip, complexity, driver, driverCount, newCircuit, circuitHours, controllerTotal, stripId, driverId]);

  useEffect(() => {
    onCalcChange?.(calc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calc]);

  return (
    <div style={st.card}>
      <div style={st.h2}>LED Strip Bid Tool</div>
      <div style={{ fontSize: 13, color: D.muted, marginBottom: 16 }}>
        Strip, channel, and driver materials + labor. Controller pricing lives in its own tool.
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px,1fr))", gap: 14, marginBottom: 16 }}>
        <Field label="Linear feet of strip">
          <input type="number" min="0" value={feet} onChange={(e) => setFeet(e.target.value)} style={st.input} />
        </Field>

        <Field label="Complexity multiplier">
          <select value={complexity} onChange={(e) => setComplexity(e.target.value)} style={st.input}>
            <option value={1.0}>1.0× — straight run</option>
            <option value={1.3}>1.3× — corners/miters</option>
            <option value={1.6}>1.6× — curves/stairs</option>
          </select>
        </Field>

        <Field label="Channel type">
          <select value={channelId} onChange={(e) => setChannelId(e.target.value)} style={st.input}>
            {CHANNEL_OPTIONS.map((c) => (
              <option key={c.id} value={c.id}>{c.label} — {currency(c.costPerFt)}/ft</option>
            ))}
          </select>
        </Field>

        <Field label="Strip type">
          <select value={stripId} onChange={(e) => setStripId(e.target.value)} style={st.input}>
            {STRIP_OPTIONS.map((s) => (
              <option key={s.id} value={s.id}>{s.label} — {currency(s.costPerFt)}/ft, {s.wattsPerFt}W/ft</option>
            ))}
          </select>
        </Field>

        <Field label="Driver type">
          <select value={driverId} onChange={(e) => setDriverId(e.target.value)} style={st.input}>
            {DRIVER_OPTIONS.map((d) => (
              <option key={d.id} value={d.id}>{d.label} — {currency(d.cost)}{d.classII ? "" : " (not Class 2)"}</option>
            ))}
          </select>
        </Field>

        <Field label="Number of drivers (auto-follows load, editable)">
          <div style={{ display: "flex", gap: 8 }}>
            <input type="number" min="0" value={driverCount} onChange={(e) => setDriverCount(e.target.value)} style={st.input} />
            {Number(driverCount) !== calc.recommendedDriverCount && (
              <button
                onClick={() => setDriverCount(calc.recommendedDriverCount)}
                style={{ ...st.btn(), marginLeft: 0, whiteSpace: "nowrap" }}
              >
                Use {calc.recommendedDriverCount}
              </button>
            )}
          </div>
        </Field>

        <Field label="New circuit/junction required?">
          <select value={newCircuit ? "yes" : "no"} onChange={(e) => setNewCircuit(e.target.value === "yes")} style={st.input}>
            <option value="yes">Yes</option>
            <option value="no">No</option>
          </select>
        </Field>

        {newCircuit && (
          <Field label="Electrician hours for new circuit">
            <input type="number" min="0" step="0.5" value={circuitHours} onChange={(e) => setCircuitHours(e.target.value)} style={st.input} />
          </Field>
        )}
      </div>

      <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: calc.wattsOk ? D.card : D.danger + "1a", border: `1px solid ${calc.wattsOk ? D.border : D.danger + "55"}`, fontSize: 12, color: calc.wattsOk ? D.muted : D.text }}>
        Power load: <strong style={{ color: D.text }}>{calc.totalWatts.toFixed(0)}W</strong> needed
        {" — "}
        <strong style={{ color: D.text }}>{calc.driverCapacity.toFixed(0)}W</strong> usable capacity
        {" "}({driverCount} × {driver.watts || 0}W driver{driverCount === 1 ? "" : "s"} @ {(DRIVER_HEADROOM * 100).toFixed(0)}% continuous)
        {!calc.wattsOk && (
          <span style={{ color: D.danger, fontWeight: 600 }}>
            {" — under-provisioned. Add drivers or split the run into more zones."}
          </span>
        )}
      </div>

      {!driver.classII && (
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: D.warning + "1a", border: `1px solid ${D.warning}55`, fontSize: 12, color: D.muted, lineHeight: 1.5 }}>
          {driver.label} exceeds the Class 2 power limit (~{CLASS_2_MAX_WATTS}W at 24V, NEC Ch. 9 Table 11(B) / UL 1310 — verify against your code cycle).
          Driver output wiring must follow a Class 1 method (typically conduit) instead of simple Class 2 low-voltage cable —
          the extra labor for that is already included in the line items below.
        </div>
      )}

      {channel.gcDisclaimer && (
        <div style={{ marginBottom: 16, padding: 12, borderRadius: 8, background: D.warning + "1a", border: `1px solid ${D.warning}55`, fontSize: 12, color: D.muted, lineHeight: 1.5 }}>
          {channel.note}
        </div>
      )}

      <table style={st.table}>
        <tbody>
          {calc.materials.map((m) => (
            <tr key={m.label}>
              <td style={{ ...st.td, color: D.muted }}>{m.label}</td>
              <td style={{ ...st.td, textAlign: "right", fontFamily: "monospace" }}>{currency(m.cost)}</td>
            </tr>
          ))}
          <tr>
            <td style={{ ...st.td, color: D.muted, fontSize: 12 }}>
              Materials subtotal (with {(MATERIAL_MARKUP * 100).toFixed(0)}% markup)
            </td>
            <td style={{ ...st.td, textAlign: "right", fontFamily: "monospace" }}>{currency(calc.materialsSell)}</td>
          </tr>
          {calc.labor.filter((l) => l.hrs > 0).map((l) => (
            <tr key={l.label}>
              <td style={{ ...st.td, color: D.muted }}>
                {l.label} ({l.hrs.toFixed(2)} hrs @ {currency(l.rate)}/hr)
              </td>
              <td style={{ ...st.td, textAlign: "right", fontFamily: "monospace" }}>{currency(l.hrs * l.rate)}</td>
            </tr>
          ))}
          {controllerTotal > 0 && (
            <tr>
              <td style={{ ...st.td, color: D.muted }}>Controller system (from Controller Tool)</td>
              <td style={{ ...st.td, textAlign: "right", fontFamily: "monospace" }}>{currency(controllerTotal)}</td>
            </tr>
          )}
          <tr>
            <td style={{ ...st.td, borderBottom: "none", fontWeight: 600, color: D.text }}>Total job price</td>
            <td style={{ ...st.td, borderBottom: "none", textAlign: "right", fontFamily: "monospace", fontWeight: 600, color: D.accent, fontSize: 15 }}>
              {currency(calc.total)}
            </td>
          </tr>
          <tr>
            <td style={{ ...st.td, borderBottom: "none", color: D.muted, fontSize: 11 }}>Price per linear foot</td>
            <td style={{ ...st.td, borderBottom: "none", textAlign: "right", color: D.muted, fontSize: 11, fontFamily: "monospace" }}>
              {calc.ft > 0 ? currency(calc.total / calc.ft) : "—"}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label style={{ display: "block" }}>
      <span style={st.label}>{label}</span>
      {children}
    </label>
  );
}
